import { ChevronLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getRouteMeta, isLandingPath, roleLanding } from "../lib/route-meta";

/**
 * Sticky sub-header shown on every non-landing page.
 * Renders a big back button + the page title so users always know
 * where they are and can retreat one step.
 */
export default function PageBar() {
    const { user } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;

    if (isLandingPath(pathname, user)) return null;

    const meta = getRouteMeta(pathname);
    if (!meta.title) return null; // unknown route → skip

    function goBack() {
        // Prefer meta-defined back target for deterministic navigation
        // on refresh / deep-links. Fallback to browser history, then role landing.
        if (meta.backTo) {
            const target = typeof meta.backTo === "function" ? meta.backTo(pathname) : meta.backTo;
            if (target) return nav(target);
        }
        const hasHistory = window.history.length > 1;
        if (hasHistory) nav(-1);
        else nav(roleLanding(user));
    }

    return (
        <div
            className="sticky top-14 z-30 bg-white/95 backdrop-blur border-b border-gray-200"
            data-testid="page-bar"
        >
            <div className="mx-auto max-w-7xl px-3 sm:px-6 h-11 flex items-center gap-2">
                <button
                    type="button"
                    onClick={goBack}
                    className="inline-flex items-center gap-1 h-9 min-w-11 px-2 -ml-1 rounded-md text-[#1E3355] hover:bg-gray-100 active:bg-gray-200 transition-colors font-medium text-sm"
                    data-testid="page-back-btn"
                    aria-label="Go back"
                >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="hidden sm:inline">Back</span>
                </button>
                <div className="h-5 w-px bg-gray-200 hidden sm:block" />
                <h1
                    className="text-sm sm:text-[15px] font-semibold text-gray-900 truncate"
                    data-testid="page-title"
                >
                    {meta.title}
                </h1>
            </div>
        </div>
    );
}
